import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { SEOHead } from '@/components/seo';
import { Header, Footer, FeesSection, FilingsSection } from '@/components/public';
import { Helmet } from 'react-helmet-async';
import { 
  ArrowRight, 
  Check, 
  ChevronRight
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SITE_NAME = 'StartEase';
const BASE_URL = 'https://starteaseai.com';
const APP_URL = '/login';

const ENTITY_CONFIG = {
  'llc': {
    name: 'LLC',
    fullName: 'Limited Liability Company',
    feeField: 'govt_fee_llc',
    description: 'The most popular choice for small businesses. Combines liability protection with tax flexibility.',
    benefits: [
      'Limited personal liability protection',
      'Pass-through taxation (no double taxation)',
      'Flexible management structure',
      'Less paperwork than corporations',
      'Credibility with customers and vendors',
      'Easy to add members or transfer ownership'
    ],
    requirements: [
      'Choose a unique business name',
      'Designate a registered agent',
      'File Articles of Organization',
      'Create an Operating Agreement',
      'Obtain an EIN from the IRS',
      'Open a business bank account'
    ],
    idealFor: ['Small business owners', 'Freelancers and consultants', 'Real estate investors', 'E-commerce businesses', 'International founders']
  },
  'c-corp': {
    name: 'C-Corporation',
    fullName: 'C-Corporation',
    feeField: 'govt_fee_ccorp',
    description: 'The preferred structure for startups raising venture capital. Unlimited growth potential.',
    benefits: [
      'Unlimited shareholders allowed',
      'Attractive to investors and VCs',
      'Stock options for employees',
      'Perpetual existence',
      'Clear ownership structure',
      'Established legal precedent'
    ],
    requirements: [
      'Choose a unique corporate name',
      'Designate a registered agent',
      'File Articles of Incorporation',
      'Create corporate Bylaws',
      'Issue stock certificates',
      'Hold initial board meeting',
      'Obtain an EIN from the IRS'
    ],
    idealFor: ['Startups raising investment', 'Companies planning to go public', 'Businesses with multiple investors', 'Tech companies offering equity', 'Companies seeking foreign investment']
  },
  'partnership': {
    name: 'Partnership',
    fullName: 'Limited Partnership',
    feeField: 'govt_fee_partnership',
    description: 'Flexible structure for businesses with multiple owners. Combines simplicity with liability options.',
    benefits: [
      'Pass-through taxation',
      'Flexible profit distribution',
      'Limited liability for limited partners',
      'Easy to form and maintain',
      'No corporate formalities required',
      'Flexible management structure'
    ],
    requirements: [
      'Choose a partnership name',
      'Designate a registered agent',
      'File Certificate of Limited Partnership',
      'Create a Partnership Agreement',
      'Obtain an EIN from the IRS',
      'Register for state taxes if required'
    ],
    idealFor: ['Professional services firms', 'Real estate investment groups', 'Family businesses', 'Joint ventures', 'Investment funds']
  }
};

const HOW_IT_WORKS = [
  { title: 'Submit Your Information', description: 'Complete our simple online form with your business details.' },
  { title: 'We Prepare Documents', description: 'Our team prepares all required formation documents.' },
  { title: 'File with the State', description: 'We submit your documents to the state for processing.' },
  { title: 'Receive Your Documents', description: 'Get your approved formation documents and next steps.' }
];

const USAStateEntityPage = () => {
  const { state: stateSlug, entity: entitySlug } = useParams();
  const [stateData, setStateData] = useState(null);
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const entityConfig = ENTITY_CONFIG[entitySlug];

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const [stateRes, faqsRes] = await Promise.all([
          axios.get(`${API}/public/state-pages/${stateSlug}`),
          axios.get(`${API}/public/faq-items`, { params: { category: 'formation', limit: 5 } })
        ]);
        
        setStateData(stateRes.data);
        setFaqs(faqsRes.data.items || []);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err.response?.status === 404 ? 'not_found' : 'error');
      } finally {
        setLoading(false);
      }
    };

    if (stateSlug && entitySlug) {
      fetchData();
    }
  }, [stateSlug, entitySlug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0b1310] text-slate-200 flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-emerald-500/40 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (!entityConfig) {
    return (
      <div className="min-h-screen bg-[#0b1310] text-slate-200">
        <Header />
        <div className="flex items-center justify-center py-32" data-testid="entity-not-found">
          <SEOHead title="Entity Type Not Found" description="The requested entity type could not be found." siteName={SITE_NAME} noindex={true} />
          <div className="text-center px-4">
            <h1 className="font-serif text-3xl font-semibold text-white mb-4">Entity Type Not Found</h1>
            <p className="text-slate-400 mb-8">The entity type you're looking for doesn't exist.</p>
            <Link to={`/usa/states/${stateSlug}/`} className="btn-corporate inline-flex items-center gap-2">
              Back to State Page <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error === 'not_found' || !stateData) {
    return (
      <div className="min-h-screen bg-[#0b1310] text-slate-200">
        <Header />
        <div className="flex items-center justify-center py-32" data-testid="state-entity-not-found">
          <SEOHead title="Page Not Found" description="The requested page could not be found." siteName={SITE_NAME} noindex={true} />
          <div className="text-center px-4">
            <h1 className="font-serif text-3xl font-semibold text-white mb-4">Page Not Found</h1>
            <p className="text-slate-400 mb-8">The state or entity combination you're looking for doesn't exist.</p>
            <Link to="/usa/" className="btn-corporate inline-flex items-center gap-2">
              View All States <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const filingFee = stateData[entityConfig.feeField] || 0;
  const timeline = `${stateData.processing_time_days} business days`;

  const pageTitle = `Form ${entitySlug === 'llc' ? 'an' : 'a'} ${entityConfig.name} in ${stateData.state_name} | $${filingFee} Filing Fee`;
  const pageDescription = `Form a ${stateData.state_name} ${entityConfig.name} from anywhere in the world. $${filingFee} state fee. ${timeline} timeline. Expert support included.`;

  const jsonLdSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": `${stateData.state_name} ${entityConfig.name} Formation`,
    "description": pageDescription,
    "provider": { "@type": "Organization", "name": SITE_NAME, "url": BASE_URL },
    "areaServed": { "@type": "State", "name": stateData.state_name },
    "offers": { "@type": "Offer", "price": filingFee, "priceCurrency": "USD" }
  };

  return (
    <div className="min-h-screen bg-[#0b1310] text-slate-200" data-testid="usa-state-entity-page">
      <SEOHead
        title={pageTitle}
        description={pageDescription}
        siteName={SITE_NAME}
        canonical={`${BASE_URL}/usa/states/${stateSlug}/${entitySlug}/`}
        keywords={`${stateData.state_name} ${entityConfig.name}, form ${entityConfig.name} in ${stateData.state_name}, ${stateData.state_code} ${entityConfig.name}`}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(jsonLdSchema)}</script>
      </Helmet>

      <Header />

      {/* Hero Section - Dark */}
      <section className="bg-[#0A0A0A]" data-testid="state-entity-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32">
          <nav className="mb-10" aria-label="Breadcrumb">
            <ol className="flex items-center gap-2 text-sm text-slate-400 flex-wrap">
              <li><Link to="/" className="hover:text-white transition-colors">Home</Link></li>
              <li><ChevronRight className="w-4 h-4" /></li>
              <li><Link to="/usa/" className="hover:text-white transition-colors">US Formation</Link></li>
              <li><ChevronRight className="w-4 h-4" /></li>
              <li><Link to={`/usa/states/${stateSlug}/`} className="hover:text-white transition-colors">{stateData.state_name}</Link></li>
              <li><ChevronRight className="w-4 h-4" /></li>
              <li className="text-emerald-400 font-medium">{entityConfig.name}</li>
            </ol>
          </nav>
          
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <p className="text-emerald-400 font-semibold text-sm uppercase tracking-widest mb-6">
                {stateData.state_code} {entityConfig.name}
              </p>
              <h1 className="font-serif text-5xl md:text-6xl text-white tracking-tight leading-[1.1] mb-8">
                {stateData.state_name} {entityConfig.name} Formation
              </h1>
              <p className="text-xl text-slate-400 leading-relaxed mb-8">
                {entityConfig.description}
              </p>
              
              <div className="flex flex-wrap gap-8 text-sm text-slate-400 mb-10">
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-emerald-400" />
                  <span><span className="text-white font-medium">${filingFee}</span> state fee</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-emerald-400" />
                  <span>{timeline}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-emerald-400" />
                  <span>Expert Support</span>
                </div>
              </div>
              
              <a href={APP_URL} className="inline-flex items-center justify-center px-8 py-4 bg-emerald-500 text-[#04110c] font-semibold hover:bg-emerald-400 transition-colors" data-testid="state-entity-hero-cta">
                Form {stateData.state_code} {entityConfig.name} Now <ArrowRight className="w-5 h-5 ml-2" />
              </a>
            </div>
            
            {/* Key Numbers Card */}
            <div className="bg-neutral-900 border border-neutral-800 p-10" data-testid="state-entity-numbers">
              <h2 className="text-xl font-semibold text-white mb-8 text-center">
                {stateData.state_name} {entityConfig.name} at a Glance
              </h2>
              
              <div className="grid grid-cols-2 gap-8">
                <div className="text-center">
                  <p className="text-slate-400 text-sm mb-2">Filing Fee</p>
                  <p className="text-3xl font-serif text-white" data-testid="entity-filing-fee">${filingFee}</p>
                </div>
                
                <div className="text-center">
                  <p className="text-slate-400 text-sm mb-2">Timeline</p>
                  <p className="text-lg font-semibold text-white">{timeline}</p>
                </div>
                
                <div className="text-center">
                  <p className="text-slate-400 text-sm mb-2">Annual Report</p>
                  <p className="text-sm font-medium text-white">{stateData.annual_report_due || 'Varies'}</p>
                </div>
                
                <div className="text-center">
                  <p className="text-slate-400 text-sm mb-2">Support</p>
                  <p className="text-sm font-medium text-white">Expert Included</p>
                </div>
              </div>
              
              <a
                href={APP_URL}
                className="mt-10 w-full inline-flex items-center justify-center px-8 py-4 bg-emerald-500 text-[#04110c] font-semibold hover:bg-emerald-400 transition-colors"
                data-testid="state-entity-card-cta"
              >
                Get Started - ${filingFee} + service fee
                <ArrowRight className="w-5 h-5 ml-2" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 md:py-32 bg-[#070d0b]" data-testid="state-entity-benefits">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="max-w-3xl mb-16">
            <p className="text-sm font-medium tracking-wider uppercase text-slate-400 mb-4">Benefits</p>
            <h2 className="font-serif text-4xl md:text-5xl font-semibold text-white tracking-tight mb-6">
              Benefits of a {stateData.state_name} {entityConfig.name}
            </h2>
            <p className="text-xl text-slate-400 leading-relaxed">
              Why business owners choose to form {entitySlug === 'llc' ? 'an' : 'a'} {entityConfig.name} in {stateData.state_name}.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {entityConfig.benefits.map((benefit, index) => (
              <div 
                key={index}
                className="bg-[#111c17] border border-white/10 p-8 flex items-start gap-4"
                data-testid={`entity-benefit-${index}`}
              >
                <div className="w-10 h-10 bg-[#111c17] flex items-center justify-center flex-shrink-0">
                  <Check className="w-5 h-5 text-white" />
                </div>
                <p className="text-slate-400">{benefit}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Requirements */}
      <section className="py-24 md:py-32 bg-[#111c17]" data-testid="state-entity-requirements">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="text-center mb-16">
            <p className="text-sm font-medium tracking-wider uppercase text-slate-400 mb-4">Requirements</p>
            <h2 className="font-serif text-4xl md:text-5xl font-semibold text-white tracking-tight mb-6">
              What You'll Need
            </h2>
            <p className="text-xl text-slate-400 leading-relaxed">
              Requirements to form a {stateData.state_name} {entityConfig.name}.
            </p>
          </div>
          
          <div className="bg-[#070d0b] border border-white/10 p-10">
            <div className="grid md:grid-cols-2 gap-6">
              {entityConfig.requirements.map((req, index) => (
                <div key={index} className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-[#111c17] text-white flex items-center justify-center flex-shrink-0 text-sm font-semibold">
                    {index + 1}
                  </div>
                  <span className="text-slate-400 pt-1">{req}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 md:py-32 bg-[#070d0b]" data-testid="state-entity-process">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="max-w-3xl mb-16">
            <p className="text-sm font-medium tracking-wider uppercase text-slate-400 mb-4">Process</p>
            <h2 className="font-serif text-4xl md:text-5xl font-semibold text-white tracking-tight mb-6">
              How We Form Your {entityConfig.name}
            </h2>
            <p className="text-xl text-slate-400 leading-relaxed">
              Simple 4-step process. We handle everything for you.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {HOW_IT_WORKS.map((step, index) => (
              <div key={index} className="relative" data-testid={`entity-step-${index + 1}`}>
                <div className="text-6xl font-serif font-semibold text-slate-300 mb-4">{index + 1}</div>
                <h3 className="text-lg font-semibold text-white mb-3">{step.title}</h3>
                <p className="text-slate-400 text-sm">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ideal For */}
      <section className="py-24 md:py-32 bg-[#111c17]" data-testid="state-entity-ideal">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="text-center mb-12">
            <p className="text-sm font-medium tracking-wider uppercase text-slate-400 mb-4">Best For</p>
            <h2 className="font-serif text-4xl md:text-5xl font-semibold text-white tracking-tight mb-6">
              {entityConfig.name} is Ideal For
            </h2>
            <p className="text-xl text-slate-400 leading-relaxed">
              A {stateData.state_name} {entityConfig.name} is perfect for these types of businesses.
            </p>
          </div>
          
          <div className="flex flex-wrap justify-center gap-4">
            {entityConfig.idealFor.map((item, index) => (
              <div 
                key={index}
                className="bg-[#070d0b] border border-white/10 px-6 py-3 flex items-center gap-2"
              >
                <Check className="w-4 h-4 text-white" />
                <span className="text-slate-400">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* State Advantages */}
      {stateData.advantages && stateData.advantages.length > 0 && (
        <section className="py-24 md:py-32 bg-[#070d0b]" data-testid="state-entity-state-advantages">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
            <div className="max-w-3xl mb-16">
              <p className="text-sm font-medium tracking-wider uppercase text-slate-400 mb-4">Advantages</p>
              <h2 className="font-serif text-4xl md:text-5xl font-semibold text-white tracking-tight mb-6">
                Why {stateData.state_name}?
              </h2>
              <p className="text-xl text-slate-400 leading-relaxed">
                Key advantages of forming your {entityConfig.name} in {stateData.state_name}.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {stateData.advantages.slice(0, 6).map((advantage, index) => (
                <div 
                  key={index}
                  className="bg-[#111c17] border border-white/10 p-8 flex items-start gap-4"
                >
                  <div className="w-10 h-10 bg-[#111c17] flex items-center justify-center flex-shrink-0">
                    <Check className="w-5 h-5 text-white" />
                  </div>
                  <p className="text-slate-400">{advantage}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Pricing Section */}
      <FeesSection 
        entityType={entitySlug}
        stateData={stateData}
        stateName={stateData.state_name}
      />

      {/* Filings Section */}
      <FilingsSection 
        entityType={entitySlug}
        stateName={stateData.state_name}
      />

      {/* FAQ Section */}
      {faqs.length > 0 && (
        <section className="py-24 md:py-32 bg-[#070d0b]" data-testid="state-entity-faqs">
          <div className="max-w-3xl mx-auto px-6 md:px-12 lg:px-16">
            <div className="text-center mb-12">
              <p className="text-sm font-medium tracking-wider uppercase text-slate-400 mb-4">FAQs</p>
              <h2 className="font-serif text-4xl font-semibold text-white tracking-tight mb-4">
                Common Questions
              </h2>
              <p className="text-xl text-slate-400">
                Frequently asked questions about {stateData.state_name} {entityConfig.name} formation.
              </p>
            </div>
            
            <div className="space-y-3">
              {faqs.map((faq, index) => (
                <Link
                  key={faq.id || index}
                  to="/faq"
                  className="block bg-[#111c17] border border-white/10 px-6 py-5 hover:border-emerald-500/40 transition-all group"
                  data-testid={`entity-faq-${index}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-white group-hover:text-emerald-400 transition-colors font-medium">
                      {faq.question}
                    </span>
                    <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-emerald-400 transition-colors flex-shrink-0 ml-4" />
                  </div>
                </Link>
              ))}
            </div>
            
            <div className="text-center mt-10">
              <Link to="/faq" className="text-white hover:text-emerald-400 font-medium inline-flex items-center gap-2 transition-colors">
                View all FAQs <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="bg-[#0A0A0A]" data-testid="state-entity-cta">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-16 py-24 md:py-32 text-center">
          <h2 className="font-serif text-4xl md:text-5xl text-white tracking-tight mb-6">
            Ready to Form Your {stateData.state_name} {entityConfig.name}?
          </h2>
          <p className="text-xl text-slate-400 mb-10 max-w-2xl mx-auto">
            Join thousands of entrepreneurs who've successfully formed their {stateData.state_name} {entityConfig.name} with {SITE_NAME}.
            State fee: ${filingFee}. Timeline: {timeline}.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={APP_URL}
              className="inline-flex items-center justify-center px-8 py-4 bg-emerald-500 text-[#04110c] font-semibold hover:bg-emerald-400 transition-colors"
              data-testid="state-entity-footer-cta"
            >
              Form {stateData.state_code} {entityConfig.name} - ${filingFee}
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
            <Link
              to={`/usa/states/${stateSlug}/`}
              className="inline-flex items-center justify-center px-8 py-4 border border-neutral-700 text-white font-medium hover:border-emerald-500 transition-colors"
            >
              Compare Entity Types
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default USAStateEntityPage;
